import React from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { AsyncQuickAddSection } from '@/_containers';
import { nameResolver } from '@/_shared';

import { Item } from './item';

import { useGetContactsMutation } from '../../apis';
import type { ContactQueryLinkerSchema } from '../../schemas';

type ContactMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<ContactQueryLinkerSchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'apiParams'
> & {
  renderItem?: QuickItemRenderer<ContactQueryLinkerSchema>;
  contactId?: string | number;
  items: ContactQueryLinkerSchema[];
  showHeader?: boolean;
  emptyDataMessage?: string;
};

export function AsyncContactMultiSelect<TFormValues extends FieldValues = FieldValues>({
  contactId,
  title = 'Contacts',
  sectionTitle = 'Contacts',
  items = [],
  emptyDataMessage = 'No contacts',
  renderItem,
  ...rest
}: ContactMultiSelectProps<TFormValues>) {
  const renderItemDefault: QuickItemRenderer<ContactQueryLinkerSchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <Item key={item.id} item={item} isEditable={isEditable} removeItemHandler={removeItem} />
    ),
    []
  );
  const itemToString = React.useCallback(
    (item: ContactQueryLinkerSchema | null) => (item ? nameResolver(item.first_name, item.last_name) : ''),
    []
  );
  const keyExtractor = React.useCallback((item: ContactQueryLinkerSchema) => item.id.toString(), []);

  const sortValues = React.useCallback(
    // TO SEE THE DIFFERENCE, CHANGE 'name' TO 'itemToString'
    (values: ContactQueryLinkerSchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const filterOptions = React.useCallback(
    (item: ContactQueryLinkerSchema) => (contactId ? item.id === Number(contactId) : false),
    [contactId]
  );

  return (
    <AsyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage={emptyDataMessage}
      items={items}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem ?? renderItemDefault}
      useApi={useGetContactsMutation}
      sortValues={sortValues}
      filterOptions={filterOptions}
    />
  );
}
